import React from 'react';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';
import apiService from '../../../../services/api/ApiService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import utilService from '../../../../services/UtilService';
import styles from './spreadSheet.scss';
import spreadSheetInteractionSelector from '../../../../selectors/question/interactions/spreadSheetInteraction/SpreadSheetInteractionSelector';
import getSpreadSheetFileIdStorageKey from '../../../../services/SpreadSheetLocalStorageKeys';

class SpreadSheetInteraction extends React.Component {
    static propTypes = {
        model: PropTypes.object.isRequired,
        contentRevisionId: PropTypes.number.isRequired,
    }

    constructor(props) {
        super(props);
        this.state = {
            wopiUrl: '',
            modifiedContent: '',
        };
    }

    async componentDidMount() {
        const { model } = this.props;
        const { itemBody } = model;
        const parser = new DOMParser();
        const doc = parser.parseFromString(itemBody, 'text/html');

        // In IIB (lockdown browser) mode the launcher fetches the WOPI URL
        // directly from platform-ia over a cert-pinned channel, so the player
        // must NOT fetch it — that would put the JWT-bearing URL into React
        // state where it is visible in devtools and potentially in XHR logs.
        if (!utilService.isIep()) {
            this.fetchWopiUrl();
        }
        this.parseIframe(doc);

        const interactionContainer = document.querySelector('.interaction-container');
        if (interactionContainer) {
            interactionContainer.remove();
        }
    }

    parseCustomInteraction(doc) {
        const customInteraction = doc.querySelector('custominteraction prompt');
        let promptText = null;
        if (customInteraction) {
            promptText = document.createElement('p');
            promptText.textContent = customInteraction.textContent;
            customInteraction.replaceWith(promptText);
        }

        const placeholder = document.createElement('div');
        placeholder.setAttribute('id', 'excel-button-placeholder');
        if (doc.body) doc.body.appendChild(placeholder);

        this.setState({ modifiedContent: doc.body.innerHTML });
    }

    parseIframe(doc) {
        const iframe = doc.querySelector('iframe');
        if (iframe) iframe.remove();
        this.parseCustomInteraction(doc);
    }

    async fetchWopiUrl() {
        const questionId = this.props.contentRevisionId;

        const response = await (
            (sessionReader.isContextExam() || sessionReader.isContextViewDelivery())
                ? apiService.fetchSpreadSheetIframeUrl()
                : apiService.fetchSpreadSheetIframeUrl(questionId)
        );

        const foundQuestion = this.extractWopiUrlQuestion(response, questionId);

        if (!foundQuestion || !foundQuestion.url) {
            throw Error('could not find current question WOPI source');
        }

        const wopiUrl = foundQuestion.url;
        this.setState({ wopiUrl });
        this.extractFileIdAndSetLocalStorage(wopiUrl);
    }

    extractWopiUrlQuestion(response, questionId) {
        if (response.questionId) {
            return response;
        }

        return response
            .questionFileUrls
            .find(r => r.questionId === questionId);
    }

    extractFileIdAndSetLocalStorage(wopiUrl) {
        const urlObj = new URL(wopiUrl);
        const wopisrc = new URLSearchParams(urlObj.search).get('wopisrc');
        let fileId;
        if (wopisrc) {
            const parts = wopisrc.split('/');
            fileId = parts[parts.length - 1].split('?')[0];
        }
        const { isSpreadSheetQuestionKey, studentFileIdKey } = getSpreadSheetFileIdStorageKey(
            this.props.contentRevisionId
        );
        localStorage.setItem(isSpreadSheetQuestionKey, true);
        localStorage.setItem(studentFileIdKey, fileId);
    }

    renderAutoSavedText() {
        return (
            <p className={styles.autoSavedText}>
                {t('excelQuestionType.autoSavedMessage')}
            </p>
        );
    }

    renderExcelButton() {
        return (
            <button
                type="button"
                className={styles.openExcelInNewWindowBtn}
                onClick={this.handleOpenInExcelForWebInNewWindow}
            >
                <img
                    src={spreadSheetInteractionSelector.excelIconSrc}
                    alt={t('excelQuestionType.BtnIcon')}
                    className={styles.excelOnlineIcon}
                />
                <span>{t('excelQuestionType.launchBtnText')}</span>
                <i className={`${styles.openExcelBtnIcon} fa fa-external-link`} aria-hidden="true" />
            </button>
        );
    }

    handleOpenInExcelForWebInNewWindow = async () => {
        const { wopiUrl } = this.state;
        const questionId = this.props.contentRevisionId;
        // In IIB mode the launcher fetches the WOPI URL — wopiUrl is not set here,
        // so allow the signal through based on isIep() alone.
        if (wopiUrl || utilService.isIep()) {
            await spreadSheetInteractionSelector.openExcelInWindowSignal(questionId, wopiUrl);
        }
    }

    render() {
        const { wopiUrl, modifiedContent } = this.state;
        const isIibSpreadsheetEnabled = marketplacePropertiesService.isO365SpreadsheetIibEnabled();
        const isIibMode = utilService.isIep();
        const showExcelFeature = wopiUrl || (isIibMode && isIibSpreadsheetEnabled);

        return (
            <div>
                <div
                    dangerouslySetInnerHTML={{
                        __html: modifiedContent,
                    }}
                />
                {showExcelFeature && this.renderExcelButton()}
                {showExcelFeature && this.renderAutoSavedText()}
            </div>
        );
    }
}

export default SpreadSheetInteraction;
